/**
 * Web Pose Detector - Browser implementation
 * MediaPipe Pose Landmarker integration for body pose detection
 */

import { PoseLandmarker, FilesetResolver, PoseLandmarkerResult } from '@mediapipe/tasks-vision';

export interface PoseKeypoint {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

export interface PoseSnapshot {
  timestamp: number;
  keypoints: PoseKeypoint[];
  worldLandmarks?: PoseKeypoint[];
}

export interface PoseQualityMetrics {
  poseDetected: boolean;
  confidence: number;
  visibility: number;
  quality: 'excellent' | 'good' | 'fair' | 'poor';
  warnings: string[];
}

export class WebPoseDetector {
  private poseLandmarker?: PoseLandmarker;
  private videoElement?: HTMLVideoElement;
  private isInitialized = false;
  private lastSnapshot?: PoseSnapshot;
  private qualityMetrics?: PoseQualityMetrics;

  async initialize(videoElement: HTMLVideoElement): Promise<void> {
    this.videoElement = videoElement;

    try {
      console.log('🏃 Loading MediaPipe Pose Landmarker...');
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );

      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      this.isInitialized = true;
      console.log('✅ MediaPipe Pose Landmarker loaded');
    } catch (error) {
      console.error('❌ Failed to load Pose Landmarker:', error);
      throw error;
    }
  }

  detect(): PoseSnapshot | null {
    if (!this.isInitialized || !this.poseLandmarker || !this.videoElement) return null;
    if (this.videoElement.readyState < 2 || this.videoElement.videoWidth === 0) return null;

    try {
      const result: PoseLandmarkerResult = this.poseLandmarker.detectForVideo(
        this.videoElement,
        performance.now()
      );

      const warnings: string[] = [];
      let poseDetected = false;
      let avgConfidence = 0;
      let avgVisibility = 0;

      if (result.landmarks && result.landmarks.length > 0) {
        poseDetected = true;
        const landmarks = result.landmarks[0];

        // Upper body indices: nose, eyes, ears, mouth, shoulders, elbows, wrists, hands
        const upperBodyIndices = [0, 2, 5, 7, 8, 9, 11, 12, 13, 14, 15, 16];
        const visible = upperBodyIndices
          .map(i => landmarks[i])
          .filter(lm => lm.visibility !== undefined && lm.visibility > 0.5);

        avgVisibility = visible.length / upperBodyIndices.length;
        avgConfidence = visible.length > 0
          ? visible.reduce((sum, lm) => sum + (lm.visibility || 0), 0) / visible.length
          : 0;

        if (avgVisibility < 0.6) warnings.push('Upper body partially occluded');
        if (avgConfidence < 0.7) warnings.push('Low tracking confidence');

        const keypoints: PoseKeypoint[] = landmarks.map(lm => ({
          x: lm.x,
          y: lm.y,
          z: lm.z,
          visibility: lm.visibility ?? 0,
        }));

        const worldLandmarks = result.worldLandmarks?.[0]?.map(lm => ({
          x: lm.x,
          y: lm.y,
          z: lm.z,
          visibility: lm.visibility ?? 0,
        }));

        this.lastSnapshot = {
          timestamp: Date.now(),
          keypoints,
          worldLandmarks,
        };
      } else {
        warnings.push('No pose detected');
        this.lastSnapshot = undefined;
      }

      let quality: 'excellent' | 'good' | 'fair' | 'poor';
      if (!poseDetected) quality = 'poor';
      else if (avgVisibility > 0.85 && avgConfidence > 0.85) quality = 'excellent';
      else if (avgVisibility > 0.7 && avgConfidence > 0.7) quality = 'good';
      else if (avgVisibility > 0.5) quality = 'fair';
      else quality = 'poor';

      this.qualityMetrics = { poseDetected, confidence: avgConfidence, visibility: avgVisibility, quality, warnings };

      return this.lastSnapshot || null;
    } catch (error) {
      console.error('❌ Pose detection error:', error);
      return null;
    }
  }

  getLastSnapshot(): PoseSnapshot | undefined {
    return this.lastSnapshot;
  }

  getQualityMetrics(): PoseQualityMetrics | undefined {
    return this.qualityMetrics;
  }

  async dispose(): Promise<void> {
    if (this.poseLandmarker) {
      this.poseLandmarker.close();
      this.poseLandmarker = undefined;
    }
    this.isInitialized = false;
  }
}
